import {
  ActionDropDown, Dropdown,
} from '@doveit/bricks';
import React from 'react';
import {
  InvoiceStatus,
  InvoiceType,
} from '../../../domain/types';
import { Invoice } from '../../../providers/api/dtos';
import InvoiceStatusBadge from '../../components/invoice-status-badge/InvoiceStatusBadge';
import { useComputedInvoiceStatus } from '../../hooks/use-computed-invoice-status/useComputedInvoiceStatus';
import { invoiceStatusColors, invoiceStatusIcons, invoiceStatusLabels } from '../../../labels';
import EmitProformaInvoiceAction from '../emit-proforma-invoice-action/EmitProformaInvoiceAction';
import MarkPaidInvoiceAction from '../mark-paid-invoice-action/MarkPaidInvoiceAction';
import MarkUnpaidInvoiceAction from '../mark-unpaid-invoice-action/MarkUnpaidInvoiceAction';
import MarkAssignmentAsCollectedAction from '../../../assignment/containers/mark-assignment-as-collected-action/MarkAssignmentAsCollectedAction';
import useAssignmentById from '../../../assignment/hooks/use-assignment-by-id/useAssignmentById';
import MarkBilledInvoiceAction from '../mark-billed-invoice-action/MarkBilledInvoiceAction';

export interface UpdateInvoiceStatusProps {
  showAsBadge?: boolean,
  showIcon?: boolean,
  invoice: Invoice,
  emphasis?: 'high' | 'low',
  size?: 'XS' | 'S',
  canEditType?: boolean,
  onSuccess?: (updatedInvoice: Invoice) => void,
}

const UpdateInvoiceStatus: React.FC<UpdateInvoiceStatusProps> = ({
  showAsBadge = false,
  showIcon = false,
  invoice,
  emphasis = 'low',
  size = 'S',
  canEditType = false,
  onSuccess,
}) => {
  const { status, canBeUpdated } = useComputedInvoiceStatus(invoice);

  const [collectedAssignmentInvoiceState, setCollectedAssignmentInvoiceState] = React.useState<Invoice>(invoice);

  const { data: assignment } = useAssignmentById(invoice.assignmentId);

  if (!canBeUpdated || showAsBadge) {
    return (
      <InvoiceStatusBadge
        status={status}
        size={size}
        emphasis={emphasis}
        showIcon={showIcon}
      />
    );
  }

  return (
    <ActionDropDown
      aria-label="Selezione dello stato pagamento"
      label={invoiceStatusLabels[status]}
      iconLeft={showIcon ? { path: invoiceStatusIcons[status] } : undefined}
      emphasis={emphasis}
      size={size}
      color={invoiceStatusColors[status]}
    >
      <EmitProformaInvoiceAction
        invoice={invoice}
        onSuccess={onSuccess}
        canEditInvoiceType={canEditType}
      >
        {({ emitProformaInvoice }) => (
          <Dropdown.Option
            label="Emetti proforma"
            onClick={emitProformaInvoice}
          />
        )}
      </EmitProformaInvoiceAction>

      {invoice.type === InvoiceType.MEDIATION && !assignment?.isCollected && (
        <MarkAssignmentAsCollectedAction
          assignmentId={invoice.assignmentId}
          onAbort={() => {
            onSuccess?.(collectedAssignmentInvoiceState);
          }}
          onSuccess={() => onSuccess?.(collectedAssignmentInvoiceState)}
        >
          {({ onClick }) => (
            <>
              <MarkPaidInvoiceAction
                invoice={invoice}
                toStatus={InvoiceStatus.COLLECTED}
                onSuccess={(updatedInvoice) => {
                  onClick();
                  setCollectedAssignmentInvoiceState(updatedInvoice);
                }}
                canEditInvoiceType={canEditType}
                aria-label="Azione per incassare il pagamento"
              >
                {({ markPaidInvoice }) => (
                  <Dropdown.Option
                    label="Incassa pagamento"
                    onClick={markPaidInvoice}
                  />
                )}
              </MarkPaidInvoiceAction>

              <MarkPaidInvoiceAction
                invoice={invoice}
                toStatus={InvoiceStatus.PAID}
                onSuccess={(updatedInvoice) => {
                  onClick();
                  setCollectedAssignmentInvoiceState(updatedInvoice);
                }}
                canEditInvoiceType={canEditType}
                aria-label="Azione per registrare il pagamento"
              >
                {({ markPaidInvoice }) => (
                  <Dropdown.Option
                    label="Registra pagamento"
                    onClick={markPaidInvoice}
                  />
                )}
              </MarkPaidInvoiceAction>
            </>
          )}
        </MarkAssignmentAsCollectedAction>
      )}

      {(invoice.type !== InvoiceType.MEDIATION || assignment?.isCollected) && (
        <>
          <MarkPaidInvoiceAction
            invoice={invoice}
            toStatus={InvoiceStatus.COLLECTED}
            onSuccess={onSuccess}
            canEditInvoiceType={canEditType}
            aria-label="Azione per incassare il pagamento"
          >
            {({ markPaidInvoice }) => (
              <Dropdown.Option
                label="Incassa pagamento"
                onClick={markPaidInvoice}
              />
            )}
          </MarkPaidInvoiceAction>

          <MarkPaidInvoiceAction
            invoice={invoice}
            toStatus={InvoiceStatus.PAID}
            onSuccess={onSuccess}
            canEditInvoiceType={canEditType}
            aria-label="Azione per registrare il pagamento"
          >
            {({ markPaidInvoice }) => (
              <Dropdown.Option
                label="Registra pagamento"
                onClick={markPaidInvoice}
              />
            )}
          </MarkPaidInvoiceAction>
        </>
      )}

      <MarkBilledInvoiceAction
        invoice={invoice}
        onSuccess={onSuccess}
      >
        {({ markBilledInvoice }) => (
          <Dropdown.Option
            label="Registra fattura"
            onClick={markBilledInvoice}
          />
        )}
      </MarkBilledInvoiceAction>

      <MarkUnpaidInvoiceAction
        invoice={invoice}
        onSuccess={onSuccess}
      >
        {({ openMarkUnpaidInvoiceModal }) => (
          <Dropdown.Option
            label="Segna come non pagato"
            onClick={openMarkUnpaidInvoiceModal}
          />
        )}
      </MarkUnpaidInvoiceAction>
    </ActionDropDown>
  );
};

export default UpdateInvoiceStatus;
